import React, { useState, useEffect } from 'react';
import api, { fileUrl, openAuthenticatedFile } from '@/api';
import { formatDate } from '@/utils/datetime';
import { Star, TrendUp, TrendDown, Trash, Plus, ArrowSquareOut, FilePdf, Trophy, ShieldCheck, Clock, Users, ChartLineUp, Percent, Lightning, MagnifyingGlass, PencilSimple, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import StartupBadges from '../../components/StartupBadges';

const MetricPill = ({ label, value, icon: Icon, positive }) => {
  if (value === null || value === undefined) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background border border-border">
      {Icon && <Icon size={14} weight="bold" className="text-text-muted flex-shrink-0" />}
      <span className="text-xs text-text-muted whitespace-nowrap">{label}</span>
      <span className={`text-sm font-mono font-semibold ml-auto ${positive === true ? 'text-success' : positive === false ? 'text-danger' : 'text-text-primary'}`}>{value}</span>
    </div>
  );
};

const BenchmarkBadge = ({ highlight }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/8 border border-primary/20">
    <Trophy size={13} weight="fill" className="text-primary flex-shrink-0" />
    <span className="text-xs text-text-secondary"><span className="font-semibold text-primary">Top {highlight.top_pct}%</span> {highlight.metric}</span>
  </div>
);

const VCTracker = ({ user, incubator = false }) => {
  const [trackedStartups, setTrackedStartups] = useState([]);
  const [trackerLists, setTrackerLists] = useState([]);
  const [activeList, setActiveList] = useState('all'); // 'all', 'uncategorized', or list_id
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [editingListId, setEditingListId] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [trackerRes, listsRes] = await Promise.all([
        api.get('/vc/tracker'),
        api.get('/vc/tracker/lists'),
      ]);
      setTrackedStartups(trackerRes.data);
      setTrackerLists(listsRes.data.lists || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handleRemove = async (startupId, startupName) => {
    if (!confirm(`Remove ${startupName} from tracker?`)) return;
    try {
      await api.delete('/vc/tracker/remove', { data: { startup_id: startupId } });
      toast.success('Removed from tracker');
      fetchAll();
    } catch (error) { toast.error('Failed to remove'); }
  };

  const createList = async () => {
    if (!newListName.trim()) return;
    try {
      await api.post('/vc/tracker/lists', { name: newListName.trim() });
      toast.success('List created!');
      setNewListName('');
      setCreatingList(false);
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const renameList = async (listId) => {
    if (!editName.trim()) { setEditingListId(null); return; }
    try {
      await api.patch(`/vc/tracker/lists/${listId}`, { name: editName.trim() });
      fetchAll();
    } catch (err) { toast.error('Failed'); }
    setEditingListId(null);
  };

  const deleteList = async (listId, name) => {
    if (!confirm(`Delete "${name}"? Startups will move to General.`)) return;
    try {
      await api.delete(`/vc/tracker/lists/${listId}`);
      toast.success('List deleted');
      if (activeList === listId) setActiveList('all');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  const openDeck = async (fileId) => {
    try {
      await openAuthenticatedFile(fileId);
    } catch (err) {
      console.error('Failed to open pitch deck:', err);
      toast.error(err.response?.data?.detail || 'Unable to open pitch deck');
    }
  };

  // Filter startups by active list + search
  const filtered = trackedStartups.filter(s => {
    if (activeList !== 'all') {
      if (activeList === 'uncategorized') {
        if (s.list_id) return false;
      } else {
        if (s.list_id !== activeList) return false;
      }
    }
    if (search && !s.startup_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary mb-2" data-testid="tracker-heading">Tracker</h1>
          <p className="text-text-secondary text-[13px]">Startups you're watching. Teaser metrics only — no exact financials.</p>
        </div>

        <div className="bg-surface border border-border rounded-[10px] p-3 mb-4">
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <ShieldCheck size={16} weight="fill" className="text-primary" />
            <span>Tracked startups share limited data. Ask the <strong>{incubator ? 'AI bar' : 'AI Chat'}</strong> to evaluate and compare pitch decks.</span>
          </div>
        </div>

        {/* List Tabs */}
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="tracker-list-tabs">
          <button onClick={() => setActiveList('all')} className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${activeList === 'all' ? 'bg-primary text-white' : 'bg-surface border border-border text-text-secondary hover:text-text-primary'}`} data-testid="tab-all">
            All ({trackedStartups.length})
          </button>
          <button onClick={() => setActiveList('uncategorized')} className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${activeList === 'uncategorized' ? 'bg-primary text-white' : 'bg-surface border border-border text-text-secondary hover:text-text-primary'}`} data-testid="tab-general">
            General ({trackedStartups.filter(s => !s.list_id).length})
          </button>
          {trackerLists.map(l => (
            <div key={l.id} className="relative group flex items-center">
              {editingListId === l.id ? (
                <div className="flex items-center gap-1">
                  <input type="text" value={editName} onChange={e => setEditName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') renameList(l.id); if (e.key === 'Escape') setEditingListId(null); }}
                    className="bg-background border border-primary rounded px-2 py-1 text-sm text-text-primary w-[120px] focus:outline-none" autoFocus />
                  <button onClick={() => renameList(l.id)} className="text-success p-1"><Check size={12} weight="bold" /></button>
                  <button onClick={() => setEditingListId(null)} className="text-text-muted p-1"><X size={12} weight="bold" /></button>
                </div>
              ) : (
                <>
                  <button onClick={() => setActiveList(l.id)} className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${activeList === l.id ? 'bg-primary text-white' : 'bg-surface border border-border text-text-secondary hover:text-text-primary'}`} data-testid={`tab-${l.id}`}>
                    {l.name} ({l.count})
                  </button>
                  <div className="hidden group-hover:flex items-center gap-0.5 ml-1">
                    <button onClick={() => { setEditingListId(l.id); setEditName(l.name); }} className="text-text-muted hover:text-text-primary p-0.5"><PencilSimple size={11} /></button>
                    <button onClick={() => deleteList(l.id, l.name)} className="text-text-muted hover:text-danger p-0.5"><Trash size={11} /></button>
                  </div>
                </>
              )}
            </div>
          ))}
          {creatingList ? (
            <div className="flex items-center gap-1">
              <input type="text" value={newListName} onChange={e => setNewListName(e.target.value)} placeholder="List name..." onKeyDown={e => { if (e.key === 'Enter') createList(); if (e.key === 'Escape') setCreatingList(false); }}
                className="bg-background border border-primary rounded-lg px-3 py-1.5 text-sm text-text-primary w-[140px] focus:outline-none" autoFocus data-testid="new-list-name" />
              <button onClick={createList} className="text-success p-1"><Check size={14} weight="bold" /></button>
              <button onClick={() => { setCreatingList(false); setNewListName(''); }} className="text-text-muted p-1"><X size={14} weight="bold" /></button>
            </div>
          ) : (
            <button onClick={() => setCreatingList(true)} className="px-3 py-1.5 text-sm rounded-lg border border-dashed border-primary/40 text-primary hover:bg-primary/5 transition-colors" data-testid="create-list-btn">
              <Plus size={14} weight="bold" className="inline mr-1" />New List
            </button>
          )}
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2.5 mb-6 w-full sm:max-w-[350px]">
          <MagnifyingGlass size={16} className="text-text-muted" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tracked startups..." className="bg-transparent text-sm text-text-primary placeholder-text-muted focus:outline-none flex-1" data-testid="tracker-search" />
          {search && <button onClick={() => setSearch('')} className="text-text-muted hover:text-text-primary"><X size={14} /></button>}
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12 text-text-secondary">Loading tracked startups...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-surface border border-border rounded-[10px] p-12 text-center" data-testid="tracker-empty">
            <Star size={48} className="text-text-muted mx-auto mb-4" weight="thin" />
            <p className="text-text-secondary mb-2">{trackedStartups.length === 0 ? 'No startups tracked yet' : 'No startups match your search'}</p>
            {trackedStartups.length === 0 && (
              <button onClick={() => window.location.href = '/vc/discover'} className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2" data-testid="go-to-discover-button">
                <Plus size={16} weight="bold" /> Discover Startups
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="tracker-grid">
            {filtered.map((s) => (
              <div key={s.startup_id} className="bg-surface border border-border rounded-[10px] p-5 hover:border-primary/30 transition-all group" data-testid={`tracked-${s.startup_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {s.logo_url ? (
                      <img src={fileUrl(s.logo_url)} className="w-11 h-11 rounded-lg object-cover flex-shrink-0" alt="" />
                    ) : (
                      <div className="w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-mono font-bold text-sm">{s.startup_name?.substring(0, 2).toUpperCase()}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading font-bold text-text-primary truncate">{s.startup_name}</h3>
                        {s.website && <a href={s.website.startsWith('http') ? s.website : `https://${s.website}`} target="_blank" rel="noopener noreferrer" className="text-text-muted hover:text-primary"><ArrowSquareOut size={14} /></a>}
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {s.sector && <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{s.sector}</span>}
                        {s.funding_stage && <span className="text-[11px] px-2 py-0.5 rounded-full bg-info/10 text-info font-medium">{s.funding_stage}</span>}
                        {s.hq_city && <span className="text-[11px] px-2 py-0.5 rounded-full bg-surface border border-border text-text-muted">{s.hq_city}</span>}
                      </div>
                      <StartupBadges badges={s.badges} size="xs" className="mt-2" />
                    </div>
                  </div>
                  <button onClick={() => handleRemove(s.startup_id, s.startup_name)} className="text-text-muted hover:text-danger transition-colors p-1.5 opacity-0 group-hover:opacity-100" title="Remove">
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
                {s.one_line_description && <p className="text-sm text-text-secondary mb-4 line-clamp-2">{s.one_line_description}</p>}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <MetricPill label="MRR Growth" value={s.mrr_growth_pct != null ? `${s.mrr_growth_pct > 0 ? '+' : ''}${s.mrr_growth_pct}%` : null} icon={s.mrr_growth_pct >= 0 ? TrendUp : TrendDown} positive={s.mrr_growth_pct != null ? s.mrr_growth_pct >= 0 : undefined} />
                  <MetricPill label="Gross Margin" value={s.gross_margin_pct != null ? `${s.gross_margin_pct}%` : null} icon={Percent} positive={s.gross_margin_pct != null ? s.gross_margin_pct >= 50 : undefined} />
                  <MetricPill label="Churn" value={s.churn_pct != null ? `${s.churn_pct}%` : null} icon={Lightning} positive={s.churn_pct != null ? s.churn_pct <= 5 : undefined} />
                  <MetricPill label="Runway" value={s.runway_range} icon={Clock} />
                  <MetricPill label="Team" value={s.team_size} icon={Users} />
                  {s.fundraising_status && s.fundraising_status !== 'Not specified' && (
                    <MetricPill label="Raising" value={s.fundraising_status === 'Yes, actively fundraising' ? 'Active' : s.fundraising_status === 'Not fundraising' ? 'No' : s.fundraising_status === 'Just closed' ? 'Closed' : s.fundraising_status} icon={ChartLineUp} positive={s.fundraising_status === 'Yes, actively fundraising'} />
                  )}
                </div>
                {s.benchmark_highlights?.length > 0 && (
                  <div className="space-y-1.5 mb-4">
                    <div className="text-[11px] text-text-muted uppercase tracking-wider font-medium mb-1">Benchmark Rankings</div>
                    {s.benchmark_highlights.map((h, i) => <BenchmarkBadge key={i} highlight={h} />)}
                  </div>
                )}
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="text-xs text-text-muted">Added {formatDate(s.added_at)}</div>
                  {s.has_pitch_deck && s.pitch_deck_file_id && (
                    <button onClick={() => openDeck(s.pitch_deck_file_id)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/8 rounded-lg hover:bg-primary/15 transition-colors">
                      <FilePdf size={14} weight="fill" /> Pitch Deck
                    </button>
                  )}
                </div>
                {s.notes && (
                  <div className="mt-3 p-3 bg-background border border-border rounded-lg">
                    <div className="text-[11px] text-text-muted mb-1 uppercase tracking-wider">Your Notes</div>
                    <div className="text-sm text-text-primary">{s.notes}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VCTracker;
